import React, { useState } from 'react';
import { Star, Upload, X, Image as ImageIcon, Video, Loader2 } from 'lucide-react';
import { Product } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../lib/api';
import toast from 'react-hot-toast';

interface AddReviewFormProps {
  product: Product;
  onReviewAdded: () => void;
  onCancel: () => void;
}

const AddReviewForm: React.FC<AddReviewFormProps> = ({ 
  product, 
  onReviewAdded, 
  onCancel 
}) => {
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [previews, setPreviews] = useState<string[]>([]);
  const [publicationConsent, setPublicationConsent] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    
    // Ограничиваем количество файлов
    if (files.length + selectedFiles.length > 5) {
      toast.error('Максимум 5 файлов');
      return;
    }

    // Проверяем размер файлов (максимум 10MB на файл)
    const oversizedFiles = selectedFiles.filter(file => file.size > 10 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      toast.error('Размер файла не должен превышать 10MB');
      return;
    }

    // Проверяем типы файлов
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'video/mp4', 'video/webm'];
    const invalidFiles = selectedFiles.filter(file => !allowedTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      toast.error('Поддерживаются только изображения (JPEG, PNG, WebP) и видео (MP4, WebM)');
      return;
    }

    const newFiles = [...files, ...selectedFiles];
    setFiles(newFiles);

    // Создаем превью
    const newPreviews = [...previews];
    selectedFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        newPreviews.push(e.target?.result as string);
        setPreviews([...newPreviews]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);
    setFiles(newFiles);
    setPreviews(newPreviews);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      toast.error('Необходимо войти в систему');
      return;
    }

    if (rating === 0) {
      toast.error('Пожалуйста, поставьте оценку');
      return;
    }

    if (title.trim().length < 3) {
      toast.error('Заголовок должен содержать минимум 3 символа');
      return;
    }

    if (comment.trim().length < 10) {
      toast.error('Комментарий должен содержать минимум 10 символов');
      return;
    }

    if (!publicationConsent) {
      toast.error('Необходимо согласие на публикацию отзыва');
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('product_id', product.id);
      formData.append('rating', String(rating));
      formData.append('title', title.trim());
      formData.append('comment', comment.trim());
      formData.append('publication_consent', String(publicationConsent));
      files.forEach((file) => formData.append('media', file));

      await apiRequest('/reviews', {
        method: 'POST',
        body: formData,
      });

      onReviewAdded();
    } catch (error: any) {
      console.error('Error creating review:', error);
      toast.error(error.message || 'Ошибка создания отзыва');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
        Оставить отзыв о товаре "{product.name}"
      </h3>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Рейтинг */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Оценка *
          </label>
          <div className="flex items-center space-x-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className="p-1 transition-colors duration-200"
              >
                <Star
                  className={`w-8 h-8 ${
                    star <= (hoverRating || rating)
                      ? 'text-yellow-400 fill-current'
                      : 'text-gray-300 dark:text-gray-600'
                  }`}
                />
              </button>
            ))}
            {rating > 0 && (
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                {rating === 1 && 'Ужасно'}
                {rating === 2 && 'Плохо'}
                {rating === 3 && 'Нормально'}
                {rating === 4 && 'Хорошо'}
                {rating === 5 && 'Отлично'}
              </span>
            )}
          </div>
        </div>

        {/* Заголовок */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Заголовок отзыва *
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Кратко опишите ваше впечатление"
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                     focus:ring-2 focus:ring-primary-500 focus:border-transparent
                     transition-colors duration-200"
            maxLength={100}
            required
          />
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {title.length}/100 символов
          </div>
        </div>

        {/* Комментарий */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Подробный отзыв *
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Расскажите подробнее о вашем опыте использования товара"
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                     focus:ring-2 focus:ring-primary-500 focus:border-transparent
                     transition-colors duration-200 resize-vertical"
            maxLength={1000}
            required
          />
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {comment.length}/1000 символов
          </div>
        </div>

        {/* Загрузка файлов */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Фото и видео (не обязательно)
          </label>
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
            <input
              type="file"
              multiple
              accept="image/*,video/*"
              onChange={handleFileChange}
              className="hidden"
              id="media-upload"
              disabled={files.length >= 5}
            />
            <label
              htmlFor="media-upload"
              className={`cursor-pointer flex flex-col items-center space-y-2 ${
                files.length >= 5 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <Upload className="w-8 h-8 text-gray-400" />
              <span className="text-sm text-gray-600 dark:text-gray-400 text-center">
                Нажмите для загрузки фото или видео<br />
                <span className="text-xs">Максимум 5 файлов, до 10MB каждый</span>
              </span>
            </label>
          </div>

          {/* Превью загруженных файлов */}
          {previews.length > 0 && (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
              {previews.map((preview, index) => (
                <div key={index} className="relative group">
                  <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
                    {files[index].type.startsWith('video/') ? (
                      <video 
                        src={preview} 
                        className="w-full h-full object-cover"
                        controls
                      />
                    ) : (
                      <img 
                        src={preview} 
                        alt={`Превью ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full
                             opacity-0 group-hover:opacity-100 transition-opacity duration-200
                             hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <div className="absolute bottom-2 left-2 flex items-center space-x-1">
                    {files[index].type.startsWith('video/') ? (
                      <Video className="w-4 h-4 text-white drop-shadow" />
                    ) : (
                      <ImageIcon className="w-4 h-4 text-white drop-shadow" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Кнопки */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <label className="sm:mr-auto flex items-start space-x-3 text-xs text-gray-500 dark:text-gray-400 max-w-md">
            <input
              type="checkbox"
              checked={publicationConsent}
              onChange={(e) => setPublicationConsent(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span>
              Я разрешаю опубликовать мое имя, текст отзыва и загруженные фото/видео на сайте согласно{' '}
              <a href="/review-publication-consent" className="text-primary-600 hover:text-primary-500 dark:text-primary-400">
                согласию на публикацию
              </a>
              .
            </span>
          </label>
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200
                     transition-colors duration-200"
            disabled={loading}
          >
            Отмена
          </button>
          <button
            type="submit"
            disabled={loading || rating === 0 || title.trim().length < 3 || comment.trim().length < 10 || !publicationConsent}
            className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            <span>{loading ? 'Отправка...' : 'Опубликовать отзыв'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddReviewForm;
